import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { getCommunicationTypes, createCommunicationType, deleteCommunicationType } from "../../lib/api";
import { TYPE_LABELS } from "../communications/constants";

export function CommunicationTypesTab() {
  const [customTypes, setCustomTypes] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await getCommunicationTypes();
      setCustomTypes(res.data);
    } catch {
      toast.error("Failed to load communication types");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const addType = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setSaving(true);
    try {
      await createCommunicationType(label);
      toast.success(`Type "${label}" added`);
      setNewLabel("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add type");
    } finally {
      setSaving(false);
    }
  };

  const removeType = async (t) => {
    if (!window.confirm(`Delete the "${t.label}" communication type?`)) return;
    try {
      await deleteCommunicationType(t.id);
      toast.success("Type deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete type");
    }
  };

  return (
    <div className="space-y-6" data-testid="communication-types-tab">
      <Card>
        <CardHeader>
          <CardTitle>Communication Types</CardTitle>
          <CardDescription>
            These categories appear in the Communication Log. Built-in types are always available; add your own to match how your school tracks contact.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h4 className="text-sm font-semibold mb-2 text-muted-foreground">Built-in types</h4>
            <div className="flex flex-wrap gap-2">
              {Object.values(TYPE_LABELS).map((label) => (
                <Badge key={label} variant="outline" className="text-sm">{label}</Badge>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold mb-2 text-muted-foreground">Custom types</h4>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : customTypes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No custom types yet. Add one below.</p>
            ) : (
              <div className="space-y-2">
                {customTypes.map((t) => (
                  <div key={t.id} className="flex items-center justify-between border rounded-lg px-3 py-2" data-testid={`comm-type-row-${t.id}`}>
                    <span className="text-sm font-medium">{t.label}</span>
                    <Button size="sm" variant="ghost" className="text-red-600 h-7 text-xs" onClick={() => removeType(t)} data-testid={`comm-type-delete-${t.id}`}>
                      Delete
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2 border-t">
            <Input
              placeholder="New type (e.g. Home Visit)"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addType(); }}
              data-testid="comm-type-new-input"
            />
            <Button onClick={addType} disabled={saving || !newLabel.trim()} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="comm-type-add-btn">
              + Add Type
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
